import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { Brand, PRODUCT_NAME } from '@/components/shared/brand';
import { phaseCountLabel } from '@/lib/phases';

const PROMISES = [
  'Leads, pipeline and quotes in one workspace',
  'Invoices, payments and a ledger that balances',
  'One login, scoped to what your role covers',
];

const STATS = [
  [phaseCountLabel, 'Modules live'],
  ['191', 'Permissions'],
  ['Double-entry', 'Ledger'],
] as const;

/**
 * The shared frame for every signed-out screen.
 *
 * Two panels: the identity on the left, the work on the right. The left panel
 * is hidden below `lg` rather than stacked — on a phone the only thing anyone
 * wants from this screen is the form.
 */
export function AuthLayout({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-screen">
      <aside className="relative hidden w-[45%] max-w-2xl shrink-0 overflow-hidden bg-sidebar px-14 py-12 lg:flex lg:flex-col">
        <div
          className="absolute -left-24 -top-28 h-[380px] w-[380px] rounded-full bg-primary/20 blur-[110px]"
          aria-hidden
        />
        <div
          className="absolute -bottom-40 -right-16 h-[420px] w-[420px] rounded-full bg-rose-500/[0.12] blur-[120px]"
          aria-hidden
        />

        <div className="relative">
          <Brand tone="dark" />
        </div>

        <div className="relative my-auto">
          <h2 className="max-w-[26rem] text-[2.6rem] font-extrabold leading-[1.06] tracking-tight text-white">
            Every lead, invoice and payment in one place.
          </h2>

          <ul className="mt-8 space-y-3">
            {PROMISES.map((promise) => (
              <li key={promise} className="flex items-start gap-2.5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span className="text-[0.9375rem] text-white/70">{promise}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative border-t border-white/10 pt-8">
          <dl className="flex gap-10">
            {STATS.map(([value, label]) => (
              <div key={label}>
                <dt className="sr-only">{label}</dt>
                <dd className="text-xl font-extrabold tracking-tight text-white">{value}</dd>
                <dd className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/40">
                  {label}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </aside>

      <main className="flex flex-1 flex-col bg-white px-6 py-8 sm:px-12">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
          <span className="lg:hidden">
            <Brand size="sm" />
          </span>
        </div>

        <div className="mx-auto my-auto w-full max-w-md py-10">
          <h1 className="text-[1.75rem] font-extrabold tracking-tight text-slate-900">{title}</h1>
          {description && <p className="mt-1.5 text-sm text-slate-500">{description}</p>}

          <div className="mt-8">{children}</div>

          {footer && <div className="mt-6 text-sm text-slate-500">{footer}</div>}
        </div>

        <p className="text-xs text-slate-400">
          © {new Date().getFullYear()} {PRODUCT_NAME}
        </p>
      </main>
    </div>
  );
}

/** Consistent inline error above a form's submit button. */
export function FormError({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {message}
    </p>
  );
}
